/**
 * ENVOYER UNE SÉLECTION DU CATALOGUE AUX ARTICLES RAPIDES — la modale de destination du relevé
 * (maquette, `qkSend`), ouverte par « Articles rapides » dans la barre de sélection.
 *
 * ⚠️ ELLE FERME LA BOUCLE. Sans elle, garnir une disposition n'était possible que depuis l'écran des
 * articles rapides, alors que le geste naturel est de choisir ses articles dans le catalogue — où on
 * les cherche, les filtre et les compare — puis de les envoyer.
 *
 * ⚠️ « +12 » ET « 3 DÉJÀ DEDANS » VIENNENT DU SERVEUR. La maquette les calcule en mémoire parce qu'elle
 * tient tout le catalogue ; ici le navigateur ne connaît pas le contenu des dispositions. Les déduire
 * du compte de tuiles donnerait un nombre plausible et faux — or c'est CE nombre qui fait choisir la
 * destination (§23.0).
 *
 * ⚠️ L'ENVOI VA AU BROUILLON DU STUDIO, jamais aux caisses : la publication réécrit la liste des caisses depuis
 * l'arbre, donc un envoi écrit à côté disparaîtrait à la publication suivante. Le gérant retrouve ses articles
 * « Non publié » à l'accueil de la disposition choisie, comme s'il les y avait posés lui-même — et la fenêtre le dit.
 *
 * ⚠️ LE CATALOGUE SE SÉLECTIONNE PAR MODÈLE, LA CAISSE AFFICHE DES DÉCLINAISONS. L'écart est dit avant
 * le clic (« 12 modèles → 47 déclinaisons ») : un modèle en six tailles et quatre coloris vaut
 * vingt-quatre tuiles, et personne ne veut le découvrir après coup.
 */
import { useEffect, useState } from 'react';
import { Modal, showToast } from '@orgazone/ui-web';
import { api, type QuickLayout } from '../api';
import { t } from '../i18n';
import { codeCaisse } from '../caisse';

type Destination = QuickLayout & { already: number };

export function EnvoiArticlesRapides({ modelKeys, onClose, onOrganiser }: {
  /** Les clés de MODÈLE sélectionnées au catalogue — le serveur les expanse en déclinaisons. */
  modelKeys: string[];
  onClose: () => void;
  /** « Ajouter et organiser » : on part sur l'écran des articles rapides, disposition ouverte. */
  onOrganiser: (layoutId: string) => void;
}) {
  const [apercu, setApercu] = useState<{ models: number; variants: number; rows: Destination[] } | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [target, setCible] = useState<string | null>(null);
  const [writing, setWriting] = useState(false);

  useEffect(() => {
    let vivant = true;
    api.quickDestinations(modelKeys)
      .then((r) => {
        if (!vivant) return;
        setApercu(r);
        // La destination proposée est la disposition PAR DÉFAUT (relevé) : c'est celle que prend une
        // caisse non rattachée, donc celle qui touche le plus de monde.
        setCible((r.rows.find((l) => l.isDefault) ?? r.rows[0])?.id ?? null);
      })
      .catch((e) => { if (vivant) setFailure(e instanceof Error ? e.message : String(e)); });
    return () => { vivant = false; };
  }, [modelKeys]);

  const envoyer = async (organiser: boolean) => {
    if (!target || writing) return;
    setWriting(true);
    try {
      const r = await api.addModelsToStudio(target, modelKeys);
      const nom = apercu?.rows.find((l) => l.id === target)?.name ?? t('la disposition');
      showToast(
        r.added > 0
          ? (r.duplicates > 0
            // ⚠️ DEUX COMPTES, DEUX CLÉS : une phrase n'accorde qu'un nombre, le second vient accordé à part.
            ? t('{n} déclinaison ajoutée au brouillon de « {nom} » — {deja}', { n: r.added, nom, deja: t('{n} y était déjà', { n: r.duplicates }) })
            : t('{n} déclinaison ajoutée au brouillon de « {nom} » — à publier depuis les articles rapides.', { n: r.added, nom }))
          : t('Déjà toutes dans « {nom} » : rien à ajouter.', { nom }),
        r.added > 0 ? 'ok' : 'info',
      );
      onClose();
      if (organiser) onOrganiser(target);
    } catch (e) {
      // ⚠️ MONTRÉ, JAMAIS AVALÉ : un refus nommé (trop de déclinaisons, aucun gencode) est la réponse
      // utile — et la modale RESTE ouverte, la sélection intacte, pour qu'on puisse en tenir compte.
      setFailure(e instanceof Error ? e.message : String(e));
    } finally { setWriting(false); }
  };

  const dest = apercu?.rows.find((l) => l.id === target) ?? null;
  const nouvelles = dest ? (apercu?.variants ?? 0) - dest.already : 0;

  return (
    <Modal
      open
      onClose={onClose}
      blockClose={() => writing}
      title={t('Ajouter aux articles rapides')}
      subtitle={apercu
        ? `${t('{n} modèle', { n: apercu.models })} — ${t('{n} déclinaison à poser.', { n: apercu.variants })} ${t('Choisissez la disposition de destination.')}`
        : t('Lecture des dispositions…')}
      icon="grid"
      tone="teal"
      width="sm"
      footer={
        <>
          {!target && !writing && apercu && apercu.rows.length > 0 && (
            <span style={{ marginRight: 'auto', fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>
              
              {t('Choisissez la disposition d’arrivée.')}
            </span>
          )}
          {/* ⚠️ TROIS NIVEAUX, comme le relevé : renoncer est un lien nu, « Ajouter » un contour,
              « Ajouter et organiser » le bouton plein. Les recettes de la charte suffisent — aucun
              habillage local, sinon ce pied de modale divergerait de tous les autres. */}
          <button type="button" className="btn-link" onClick={onClose}>{t('Annuler')}</button>
          <button
            type="button" className="btn-ghost"
            onClick={() => void envoyer(false)} aria-disabled={!target || writing}
          >
            {writing ? t('Ajout…') : t('Ajouter')}
          </button>
          <button
            type="button" className="btn-cta"
            onClick={() => void envoyer(true)} aria-disabled={!target || writing}
          >
            
            {t('Ajouter et organiser')}
          </button>
        </>
      }
    >
      <div style={{ display: 'grid', gap: 8 }}>
        {apercu === null && !failure && (
          <p style={{ margin: 0, fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>{t('Lecture des dispositions…')}</p>
        )}
        {apercu?.rows.length === 0 && (
          <p style={{ margin: 0, fontSize: 'var(--t-meta)', color: 'var(--ink-500)', lineHeight: 1.5 }}>
            
            {t('Ce magasin n’a aucune disposition d’articles rapides. Créez-en une depuis l’écran « Articles rapides » — la première créée devient celle que prend toute caisse.')}
          </p>
        )}
        {apercu?.rows.map((l) => {
          const on = l.id === target;
          const neuves = apercu.variants - l.already;
          return (
            <button
              key={l.id} type="button" onClick={() => setCible(l.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 11, padding: '11px 13px', textAlign: 'left',
                border: `1.5px solid ${on ? 'var(--teal-500)' : 'var(--ink-200)'}`,
                borderRadius: 'var(--r-sm)', background: on ? 'var(--teal-100)' : 'var(--paper)',
                cursor: 'pointer',
              }}
            >
              <span
                aria-hidden
                style={{
                  width: 17, height: 17, borderRadius: 'var(--r-pill)', boxSizing: 'border-box', flexShrink: 0,
                  border: `1.5px solid ${on ? 'var(--teal-500)' : 'var(--ink-300)'}`,
                  background: 'var(--paper)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                {on && <span style={{ width: 9, height: 9, borderRadius: 'var(--r-pill)', background: 'var(--teal-500)' }} />}
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--ink-900)' }}>{l.name}</span>
                  {l.isDefault && (
                    <span style={{
                      fontSize: 'var(--t-micro)', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase',
                      color: 'var(--ink-500)', background: 'var(--ink-100)', borderRadius: 'var(--r-xs)', padding: '2px 6px',
                    }}>{t('Standard')}</span>
                  )}
                </span>
                <span style={{ display: 'block', fontSize: '11.5px', color: 'var(--ink-500)', marginTop: 2 }}>
                  {l.registers.length > 0 ? l.registers.map(codeCaisse).join(' · ') : t('aucune caisse')}
                  {' · '}
                  {l.already > 0
                    ? t('{n} tuile · {already} déjà dedans', { n: l.tiles, already: l.already })
                    : t('{n} tuile', { n: l.tiles })}
                </span>
              </span>
              {/* Le gain RÉEL de cette destination — compté en base, jamais déduit du compte de tuiles. */}
              <span style={{
                fontSize: 'var(--t-dense)', fontWeight: 700, flexShrink: 0, borderRadius: 'var(--r-pill)', padding: '4px 9px',
                fontVariantNumeric: 'tabular-nums',
                color: neuves > 0 ? 'var(--teal-800)' : 'var(--ink-500)',
                background: neuves > 0 ? 'var(--teal-100)' : 'var(--ink-100)',
              }}>
                {neuves > 0 ? `+${neuves}` : t('déjà tous')}
              </span>
            </button>
          );
        })}
        {dest && nouvelles === 0 && (
          <p style={{ margin: 0, fontSize: 'var(--t-meta)', color: 'var(--ink-500)', lineHeight: 1.5 }}>
            
            {t('Tout est déjà dans « {name} » : l’ajout ne changerait rien.', { name: dest.name })}
          </p>
        )}
        {failure && (
          <p role="alert" style={{ margin: 0, fontSize: 'var(--t-meta)', fontWeight: 600, color: 'var(--danger-ink)', lineHeight: 1.5 }}>
            {failure}
          </p>
        )}
      </div>
    </Modal>
  );
}
